// src/components/TopMenu.js
import React, { useContext, useEffect, useRef } from 'react';
import { Navbar, Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { jwtDecode } from 'jwt-decode';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in ms

function TopMenu() {
  const navigate = useNavigate();
  const { user, tokens, logout } = useContext(AuthContext);

  // 1) Session‑expiry logout (as before)
  useEffect(() => {
    if (!tokens?.access_token) return;

    let timerId;
    try {
      const { exp } = jwtDecode(tokens.access_token);
      const msUntilExpiry = exp * 1000 - Date.now();

      if (msUntilExpiry <= 0) {
        logout();
        navigate('/');
      } else {
        timerId = setTimeout(() => {
          logout();
          navigate('/');
        }, msUntilExpiry);
      }
    } catch (err) {
      ////console.error('Failed to schedule session timeout:', err);
      logout();
      navigate('/');
    }

    return () => clearTimeout(timerId);
  }, [tokens?.access_token, logout, navigate]);

  // 2) Inactivity‑based logout
  const inactivityTimer = useRef();
  useEffect(() => {
    if (!user) return; // only track when logged in

    const resetInactivity = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        logout();
        navigate('/');
      }, INACTIVITY_TIMEOUT);
    };

    // events that count as “activity”
    const activityEvents = [
      'mousemove', 'mousedown', 'keydown',
      'touchstart', 'scroll'
    ];

    // bind & initialize
    activityEvents.forEach(evt =>
      document.addEventListener(evt, resetInactivity)
    );
    resetInactivity();

    // cleanup
    return () => {
      clearTimeout(inactivityTimer.current);
      activityEvents.forEach(evt =>
        document.removeEventListener(evt, resetInactivity)
      );
    };
  }, [user, logout, navigate]);

  // Environment‑based URLs
  const {
    REACT_APP_COGNITO_DOMAIN,
    REACT_APP_COGNITO_CLIENT_ID,
    REACT_APP_COGNITO_REDIRECT_URI,
  } = process.env;

  if (
    !REACT_APP_COGNITO_DOMAIN ||
    !REACT_APP_COGNITO_CLIENT_ID ||
    !REACT_APP_COGNITO_REDIRECT_URI
  ) {
    ////console.error('Cognito environment variables are missing. Please check your .env file.');
  }

  // Login & Sign‑up URLs
  const loginUrl =
    `${REACT_APP_COGNITO_DOMAIN}/login?response_type=code&client_id=${REACT_APP_COGNITO_CLIENT_ID}` +
    `&redirect_uri=${encodeURIComponent(REACT_APP_COGNITO_REDIRECT_URI)}` +
    `&scope=openid+email+phone`;

    const signUpUrl =
       `${REACT_APP_COGNITO_DOMAIN}/signup?response_type=code&client_id=${REACT_APP_COGNITO_CLIENT_ID}` +
       `&redirect_uri=${encodeURIComponent(REACT_APP_COGNITO_REDIRECT_URI)}` +
       `&scope=openid+email+phone`;

  const handleLogout = e => {
    e.preventDefault();
    logout();
    navigate('/');
  };

  return (
    <Navbar bg="dark" variant="dark" expand="lg" className="shadow-sm">
      <Navbar.Brand href="/">
        <img
          src="./assets/logo.png"
          alt="Logo"
          width="30"
          height="30"
          className="d-inline-block align-top"
        />{' '}
        Precise Draft <span className="text-xs text-gray-300"></span>
      </Navbar.Brand>
      <Navbar.Toggle aria-controls="basic-navbar-nav" />
      <Navbar.Collapse id="basic-navbar-nav">
        <Nav className="ms-auto d-flex align-items-center">
          <Nav.Link href="/" className="text-white me-3">Home</Nav.Link>
          <Nav.Link href="/leads" className="text-white me-3">Lead Gen</Nav.Link>
          <Nav.Link href="/faq" className="text-white me-3">FAQ</Nav.Link>
          <Nav.Link href="/blogs" className="text-white me-3">Blogs</Nav.Link> 
          <Nav.Link href="/contactUs" className="text-white me-3">Contact</Nav.Link>


          {user ? (
            <>
              <Nav.Link href="/dash" className="text-white me-3">
                Dashboard
              </Nav.Link>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center justify-center px-2.5 py-1.5 text-sm leading-4 rounded-md border border-transparent transition-all duration-200 text-white bg-red-500 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 font-semibold"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Nav.Link href={loginUrl} className="text-white me-3">
                Login
              </Nav.Link>
              <Nav.Link href={signUpUrl} className="text-white me-3">
                Sign Up
              </Nav.Link>
            </>
          )}
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
}

export default TopMenu;
